import { useState, useMemo, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useReactTable, getCoreRowModel, type ColumnDef } from '@tanstack/react-table';
import {
  MapPin,
  MoreHorizontal,
  Pencil,
  Plus,
  Search,
  ShieldCheck,
  UserCheck,
  UserX,
  Users,
  Warehouse,
} from 'lucide-react';
import { roleLabel } from '@supernova/shared';
import { PageHeader } from '@/components/shared/page-header';
import { LocationBadge } from '@/components/shared/location-badge';
import { DataTable } from '@/components/data-table/data-table';
import { DataTablePagination } from '@/components/data-table/data-table-pagination';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { useUsers, useUpdateUser } from '@/hooks/use-users';
import { useLocations } from '@/hooks/use-locations';
import { useLocationStore } from '@/stores/location-store';
import { cn, formatDateTime } from '@/lib/utils';
import { isWarehouseType } from '@/lib/location-type';
import { useUserAccess } from './user-access';

const PER_PAGE_OPTIONS = [10, 25, 50, 100];

type StatusTab = 'active' | 'inactive';

const selectClass =
  'h-9 rounded-lg border border-input bg-white px-3 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary';

/** `#/users?status=inactive` opens on the Inactive tab (linked from Add User). */
function initialTab(): StatusTab {
  const params = new URLSearchParams(window.location.hash.split('?')[1] ?? '');
  return params.get('status') === 'inactive' ? 'inactive' : 'active';
}

const roleNameOf = (u: any): string => u.customRoleName || roleLabel(u.role);

function initials(name: string) {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  return (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
}

/**
 * The row's ⋯ menu. It opens with fixed positioning so the table's horizontal
 * scroll box can't clip it, and closes on Escape, an outside click, or scroll.
 */
function RowActionsMenu({ label, children }: { label: string; children: (close: () => void) => React.ReactNode }) {
  const [pos, setPos] = useState<{ top: number; right: number } | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const close = () => setPos(null);

  useEffect(() => {
    if (!pos) return;
    menuRef.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
    const onDown = (e: MouseEvent) => {
      if (!menuRef.current?.contains(e.target as Node) && !buttonRef.current?.contains(e.target as Node)) close();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        close();
        buttonRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [pos]);

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={!!pos}
        onClick={() => {
          if (pos) return close();
          const rect = buttonRef.current!.getBoundingClientRect();
          setPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
        }}
        className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-border bg-white text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
      </button>
      {/* Rendered on document.body: inside the pinned Actions cell the menu
          sits in that cell's layer, and the next row's pinned cell paints over it. */}
      {pos &&
        createPortal(
          <div
            ref={menuRef}
            role="menu"
            aria-label={label}
            style={{ top: pos.top, right: pos.right }}
            className="fixed z-50 min-w-[11rem] rounded-lg border border-border bg-white p-1 shadow-lg"
          >
            {children(close)}
          </div>,
          document.body,
        )}
    </>
  );
}

export function UsersPage() {
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState<StatusTab>(initialTab);
  const [roleFilter, setRoleFilter] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [confirmDialog, setConfirmDialog] = useState<{ userId: string; userName: string } | null>(null);

  const { user, canCreate, canEdit, canDeactivate, canManageRoles, canActOn, roleOptions } = useUserAccess();
  const { selectedLocationId } = useLocationStore();
  const { data: locationsData } = useLocations();
  const locations = locationsData?.data ?? [];

  // Fetch all users from API (large perPage to get all for client-side filtering)
  const { data, isLoading, isError, refetch } = useUsers({
    page: 1,
    perPage: 500,
    search: search || undefined,
    locationId: selectedLocationId || undefined,
  });

  const updateUser = useUpdateUser();

  const allUsers = useMemo(() => data?.data ?? [], [data]);

  // Role filter lists the roles people on this page actually hold.
  const roleFilterOptions = useMemo(() => Array.from(new Set(allUsers.map(roleNameOf))).sort() as string[], [allUsers]);

  // Client-side filtering by status tab, role and location
  const filteredUsers = useMemo(() => {
    return allUsers.filter((u: any) => {
      if (activeTab === 'active' ? !u.isActive : u.isActive) return false;
      if (roleFilter && roleNameOf(u) !== roleFilter) return false;
      // No locations on a user means all locations.
      if (locationFilter && (u.locationIds ?? []).length > 0 && !u.locationIds.includes(locationFilter)) return false;
      return true;
    });
  }, [allUsers, activeTab, roleFilter, locationFilter]);

  // Client-side pagination
  const totalFiltered = filteredUsers.length;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / perPage));
  const paginatedUsers = useMemo(() => {
    const start = (page - 1) * perPage;
    return filteredUsers.slice(start, start + perPage);
  }, [filteredUsers, page, perPage]);

  // Count for tab badges
  const activeCount = useMemo(() => allUsers.filter((u: any) => u.isActive).length, [allUsers]);
  const inactiveCount = useMemo(() => allUsers.filter((u: any) => !u.isActive).length, [allUsers]);

  const handleToggleActive = async (userId: string, currentActive: boolean, userName: string) => {
    if (currentActive) {
      // Show confirmation dialog before deactivating
      setConfirmDialog({ userId, userName });
    } else {
      // Activate immediately without confirmation
      await updateUser.mutateAsync({ id: userId, isActive: true });
    }
  };

  const handleConfirmDeactivate = async () => {
    if (!confirmDialog) return;
    await updateUser.mutateAsync({ id: confirmDialog.userId, isActive: false });
    setConfirmDialog(null);
  };

  const handleTabChange = (tab: StatusTab) => {
    setActiveTab(tab);
    setPage(1);
  };

  const handlePerPageChange = (newPerPage: number) => {
    setPerPage(newPerPage);
    setPage(1);
  };

  const filtersOn = !!(search || roleFilter || locationFilter);
  const clearFilters = () => {
    setSearch('');
    setRoleFilter('');
    setLocationFilter('');
    setPage(1);
  };

  const columns: ColumnDef<any>[] = [
    {
      accessorKey: 'name',
      header: 'User',
      cell: ({ row }) => {
        const isSelf = user?.id === row.original.id;
        return (
          <div className="flex min-w-0 items-center gap-3">
            <span
              aria-hidden="true"
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary"
            >
              {initials(row.original.name)}
            </span>
            <div className="min-w-0">
              <p className="truncate font-medium text-foreground">
                {row.original.name}
                {isSelf && <span className="font-normal text-muted-foreground"> (you)</span>}
              </p>
              <p className="truncate text-xs text-muted-foreground">{row.original.email}</p>
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: 'role',
      header: 'Role',
      cell: ({ row }) => {
        const extras: string[] = row.original.extraPermissions || [];
        return (
          <span className="inline-flex flex-wrap items-center gap-1.5">
            <span className="inline-flex h-6 items-center rounded-md border border-border bg-white px-2 text-xs font-medium text-foreground">
              {roleNameOf(row.original)}
            </span>
            {row.original.customRoleId && <span className="text-xs text-muted-foreground">Custom</span>}
            {extras.length > 0 && (
              <span
                className="rounded-md bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800"
                title="Extra permissions on top of the role"
              >
                +{extras.length} extra
              </span>
            )}
          </span>
        );
      },
    },
    {
      accessorKey: 'locationIds',
      header: 'Locations',
      cell: ({ row }) => {
        const locIds: string[] = row.original.locationIds || [];
        if (locIds.length === 0) {
          return <span className="text-xs text-muted-foreground">All locations</span>;
        }
        return (
          <div className="flex flex-wrap gap-1">
            {locIds.map((locId: string) => {
              const loc = locations.find((l: any) => l.id === locId);
              const isWarehouse = isWarehouseType(loc?.type);
              return (
                <span
                  key={locId}
                  className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${
                    isWarehouse ? 'bg-orange-50 text-orange-700' : 'bg-blue-50 text-blue-700'
                  }`}
                >
                  {isWarehouse ? (
                    <Warehouse className="h-3 w-3" aria-hidden="true" />
                  ) : (
                    <MapPin className="h-3 w-3" aria-hidden="true" />
                  )}
                  {loc?.nickname || loc?.name || locId.slice(0, 8)}
                </span>
              );
            })}
          </div>
        );
      },
    },
    {
      accessorKey: 'createdAt',
      header: 'Created',
      cell: ({ row }) => (
        <span className="whitespace-nowrap text-muted-foreground">{formatDateTime(row.original.createdAt)}</span>
      ),
    },
    {
      id: 'actions',
      header: () => <span className="block text-right">Actions</span>,
      size: 120,
      meta: { sticky: 'right' },
      cell: ({ row }) => {
        if (!canActOn(row.original) || !(canEdit || canDeactivate)) return null;
        // Logged-in user cannot deactivate their own account
        const isSelf = user?.id === row.original.id;
        const showMenu = canDeactivate && !(isSelf && row.original.isActive);
        return (
          <div className="flex items-center justify-end gap-1">
            {showMenu && (
              <RowActionsMenu label={`More actions for ${row.original.name}`}>
                {(close) => (
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      close();
                      handleToggleActive(row.original.id, row.original.isActive, row.original.name);
                    }}
                    className={cn(
                      'flex h-9 w-full items-center gap-2 rounded-md px-3 text-left text-sm font-medium focus:bg-muted focus:outline-none hover:bg-muted',
                      row.original.isActive ? 'text-red-700' : 'text-foreground',
                    )}
                  >
                    {row.original.isActive ? (
                      <>
                        <UserX className="h-4 w-4" aria-hidden="true" /> Deactivate
                      </>
                    ) : (
                      <>
                        <UserCheck className="h-4 w-4" aria-hidden="true" /> Activate
                      </>
                    )}
                  </button>
                )}
              </RowActionsMenu>
            )}
            {canEdit && (
              <a
                href={`#/users/${row.original.id}/edit`}
                title="Edit"
                aria-label={`Edit ${row.original.name}`}
                className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-border bg-white text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <Pencil className="h-4 w-4" aria-hidden="true" />
              </a>
            )}
          </div>
        );
      },
    },
  ];

  const table = useReactTable({ data: paginatedUsers, columns, getCoreRowModel: getCoreRowModel() });

  const tabButton = (tab: StatusTab, label: string, count: number) => {
    const selected = activeTab === tab;
    return (
      <button
        type="button"
        role="tab"
        aria-selected={selected}
        onClick={() => handleTabChange(tab)}
        className={cn(
          'inline-flex h-8 items-center gap-1.5 rounded-md px-3 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          selected ? 'bg-white text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground',
        )}
      >
        {label}
        <span className={cn('text-xs', selected ? 'text-primary' : 'text-muted-foreground')}>{count}</span>
      </button>
    );
  };

  const empty = filtersOn
    ? {
        icon: Search,
        title: 'No users match',
        description: 'Try another name, email, role or location.',
        action: (
          <button
            type="button"
            onClick={clearFilters}
            className="inline-flex h-9 items-center rounded-lg border border-border bg-white px-4 text-sm font-medium hover:bg-muted"
          >
            Clear filters
          </button>
        ),
      }
    : activeTab === 'inactive'
      ? { icon: UserX, title: 'No inactive users', description: 'People you deactivate show up here.' }
      : { icon: Users, title: 'No users yet', description: 'Add your first team member to get started.' };

  return (
    <div className="space-y-4">
      <PageHeader
        title="User Management"
        description="Add team members, choose their role, and control which locations they can access."
      >
        <div className="flex flex-wrap items-center gap-3">
          <LocationBadge />
          {canManageRoles && (
            <a
              href="#/users/roles"
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-lg border border-border bg-white px-4 py-2 text-sm font-medium hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <ShieldCheck className="h-4 w-4" aria-hidden="true" />
              Roles and Permissions
            </a>
          )}
          {canCreate && roleOptions.length > 0 && (
            <a
              href="#/users/new"
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              <Plus className="h-4 w-4" aria-hidden="true" />
              Add User
            </a>
          )}
        </div>
      </PageHeader>

      <div className="rounded-xl border border-border bg-white">
        <div className="flex flex-wrap items-center gap-2 border-b border-border p-3 sm:p-4">
          <div role="tablist" aria-label="User status" className="inline-flex rounded-lg bg-muted p-1">
            {tabButton('active', 'Active', activeCount)}
            {tabButton('inactive', 'Inactive', inactiveCount)}
          </div>
          <div className="relative min-w-[12rem] flex-1 sm:max-w-sm">
            <Search
              className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
              aria-hidden="true"
            />
            <input
              type="search"
              aria-label="Search users"
              placeholder="Search by name or email"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              className="h-9 w-full rounded-lg border border-input bg-background pl-9 pr-4 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
            />
          </div>
          <select
            aria-label="Filter by role"
            value={roleFilter}
            onChange={(e) => {
              setRoleFilter(e.target.value);
              setPage(1);
            }}
            className={selectClass}
          >
            <option value="">All roles</option>
            {roleFilterOptions.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
          {locations.length > 1 && (
            <select
              aria-label="Filter by location"
              value={locationFilter}
              onChange={(e) => {
                setLocationFilter(e.target.value);
                setPage(1);
              }}
              className={selectClass}
            >
              <option value="">All locations</option>
              {locations.map((loc: any) => (
                <option key={loc.id} value={loc.id}>
                  {loc.nickname || loc.name}
                </option>
              ))}
            </select>
          )}
        </div>

        {isLoading ? (
          <div className="flex h-64 items-center justify-center text-muted-foreground">Loading...</div>
        ) : isError ? (
          <div className="flex h-64 flex-col items-center justify-center gap-3 text-muted-foreground">
            <p className="text-sm">Failed to load users. Please try again.</p>
            <button
              onClick={() => refetch()}
              className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
            >
              Retry
            </button>
          </div>
        ) : (
          <div className="p-3 sm:p-4">
            <DataTable table={table} columns={columns} empty={empty} />
            {totalFiltered > 0 && (
              <DataTablePagination
                page={page}
                totalPages={totalPages}
                total={totalFiltered}
                perPage={perPage}
                onPageChange={setPage}
                perPageOptions={PER_PAGE_OPTIONS}
                onPerPageChange={handlePerPageChange}
              />
            )}
          </div>
        )}
      </div>

      {/* Deactivate Confirmation Dialog */}
      <ConfirmDialog
        open={!!confirmDialog}
        title="Deactivate User"
        message={`Are you sure you want to deactivate "${confirmDialog?.userName}"? This user will no longer be able to access the system.`}
        confirmLabel="Deactivate"
        cancelLabel="Cancel"
        variant="danger"
        loading={updateUser.isPending}
        onConfirm={handleConfirmDeactivate}
        onCancel={() => setConfirmDialog(null)}
      />
    </div>
  );
}
